import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import CurrencyIcon from "@/components/CurrencyIcon";
import { toast } from "sonner";
import { Plus, ShieldCheck, QrCode, Sparkle, Trash, User, Copy, Bell, BellRinging, BellSlash } from "@phosphor-icons/react";
import { ensurePushSubscription, disablePush, resetAppCache } from "@/lib/push";

const TABS = [
  { v: "users",         label: "Users",         roles: ["admin"] },
  { v: "brands",        label: "Brands",        roles: ["admin", "requester"] },
  { v: "vendors",       label: "Vendors",       roles: ["admin", "requester"] },
  { v: "payer_vendors", label: "Payer Vendors", roles: ["admin", "payer"] },
  { v: "expense_types", label: "Expense Types", roles: ["admin", "requester"] },
  { v: "currencies",    label: "Currencies",    roles: ["admin", "requester"] },
  { v: "security",      label: "Security",      roles: ["admin"] },
  { v: "ai",            label: "AI",            roles: ["admin"] },
  { v: "demo",          label: "Demo mode",     roles: ["admin"] },
  { v: "totp",          label: "My Account",    roles: ["admin", "requester", "auditor", "payer", "approver"] },
];

export default function Admin() {
  const { user } = useAuth();
  const visibleTabs = TABS.filter((t) => t.roles.includes(user?.role));
  const defaultTab = visibleTabs[0]?.v || "totp";
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]">
      <header className="mb-6">
        <div className="overline">Configuration</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">
          <span className="text-gold-gradient">{user?.role === "admin" ? "Admin" : "Settings"}</span>
        </h1>
      </header>
      <Tabs defaultValue={defaultTab}>
        <div className="-mx-4 sm:mx-0 overflow-x-auto pb-1">
          <TabsList className="rounded-xl flex-nowrap sm:flex-wrap h-auto glass p-1 gap-1 whitespace-nowrap w-max sm:w-full">
            {visibleTabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`}
                className="tab-pill rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold shrink-0">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {visibleTabs.some((t) => t.v === "users")      && <TabsContent value="users"      className="mt-6"><UsersTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "brands")     && <TabsContent value="brands"     className="mt-6"><BrandsTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "vendors")       && <TabsContent value="vendors"       className="mt-6"><VendorsTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "payer_vendors") && <TabsContent value="payer_vendors" className="mt-6"><PayerVendorsTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "expense_types") && <TabsContent value="expense_types" className="mt-6"><ExpenseTypesTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "currencies") && <TabsContent value="currencies" className="mt-6"><CurrenciesTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "security")   && <TabsContent value="security"   className="mt-6"><SecurityTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "ai")         && <TabsContent value="ai"         className="mt-6"><AiKeyTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "demo")       && <TabsContent value="demo"       className="mt-6"><DemoTab /></TabsContent>}
        {visibleTabs.some((t) => t.v === "totp")       && <TabsContent value="totp"       className="mt-6"><TotpTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

/* --- VENDORS --- */
function VendorsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", notes: "" });
  const load = useCallback(async () => {
    // Non-admin can only see active items via the plain endpoint.
    // /vendors/all is admin-only and returns 403 for requesters — which was
    // making the list appear permanently empty for them.
    const url = isAdmin ? "/vendors/all" : "/vendors";
    const r = await api.get(url);
    setItems(r.data);
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing(null); setForm({ name: "", code: "", notes: "" }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name: v.name, code: v.code || "", notes: v.notes || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) { await api.patch(`/vendors/${editing.id}`, form); toast.success("Vendor updated"); }
      else { await api.post("/vendors", form); toast.success("Vendor created"); }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (v) => {
    try {
      if (v.is_active) { await api.delete(`/vendors/${v.id}`); toast.success("Vendor disabled"); }
      else { await api.patch(`/vendors/${v.id}`, { is_active: true }); toast.success("Vendor enabled"); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const hardDelete = async (v) => {
    if (!window.confirm(`Permanently delete vendor "${v.name}"? This cannot be undone. If any payment references this vendor, deletion will be blocked.`)) return;
    try {
      await api.delete(`/vendors/${v.id}?hard=true`);
      toast.success("Vendor deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (items === null) return <TableSkeleton rows={4} />;
  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="text-sm text-slate-600">{items.length} vendors</div>
        <Button onClick={openNew} data-testid="admin-new-vendor" className="rounded-xl amber-pill h-10 px-4"><Plus size={16} className="mr-1.5" weight="bold" /> New vendor</Button>
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="vendors-mobile-list">
        {items.map((v) => (
          <AdminMobileCard
            key={v.id}
            testid={`vendor-card-${v.name}`}
            title={v.name}
            subtitle={v.code || null}
            badges={[
              isAdmin && (v.is_active
                ? { label: "Active", tone: "bg-emerald-100 border-emerald-300 text-emerald-800" }
                : { label: "Disabled", tone: "bg-slate-200 border-slate-300 text-slate-700" }),
            ]}
            meta={[
              v.notes && { label: "Notes", value: v.notes, wide: true },
            ]}
            actions={isAdmin ? (
              <>
                <AdminActionBtn tone="amber" onClick={() => openEdit(v)} data-testid={`vendor-edit-mob-${v.name}`}>Edit</AdminActionBtn>
                {v.is_active ? (
                  <AdminActionBtn tone="orange" onClick={() => toggleActive(v)} data-testid={`vendor-toggle-mob-${v.name}`}>Disable</AdminActionBtn>
                ) : (
                  <AdminActionBtn tone="emerald" onClick={() => toggleActive(v)} data-testid={`vendor-toggle-mob-${v.name}`}>Enable</AdminActionBtn>
                )}
                <AdminActionBtn tone="red" icon={Trash} onClick={() => hardDelete(v)} data-testid={`vendor-delete-mob-${v.name}`}>Delete</AdminActionBtn>
              </>
            ) : null}
          />
        ))}
        {items.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No vendors yet. Tap New vendor to add your first.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="w-full ledger-table text-sm">
          <thead className="bg-white/40"><tr className="text-left">{(isAdmin ? ["Name","Code","Notes","Active","Actions"] : ["Name","Code","Notes"]).map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
          <tbody>{items.map((v) => (
            <tr key={v.id} data-testid={`vendor-row-${v.name}`}>
              <td className="px-4 py-3 font-medium">{v.name}</td>
              <td className="px-4 py-3 font-mono text-xs">{v.code || "—"}</td>
              <td className="px-4 py-3 text-xs text-slate-600">{v.notes || "—"}</td>
              {isAdmin && <td className="px-4 py-3 text-xs">{v.is_active ? "YES" : "NO"}</td>}
              {isAdmin && (
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(v)} data-testid={`vendor-edit-${v.name}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50">Edit</button>
                  <button onClick={() => toggleActive(v)} data-testid={`vendor-toggle-${v.name}`} className={`px-2 py-1 rounded-lg text-xs font-semibold ${v.is_active ? "text-orange-700 hover:bg-orange-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{v.is_active ? "Disable" : "Enable"}</button>
                </td>
              )}
            </tr>
          ))}{items.length === 0 && (<tr><td colSpan={isAdmin ? 5 : 3} className="px-4 py-8 text-center text-sm text-slate-500">No vendors yet. Click New vendor to add your first.</td></tr>)}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit vendor · ${editing.name}` : "New vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="vendor-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Moshi Moshi Marketing" /></div>
            <div><Label>Code (optional)</Label><Input data-testid="vendor-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
            <div><Label>Notes</Label><Input data-testid="vendor-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="vendor-form-save" className="rounded-xl amber-pill">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --- Shared skeleton --- */
function TableSkeleton({ rows = 4 }) {
  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid="admin-loading-skeleton">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-12 rounded-xl bg-white/40 animate-pulse" />
      ))}
    </div>
  );
}

/* --- Mobile-friendly primitives for admin tabs --- */
// Card row: single-column, edge-to-edge on phone, tap-target-sized action buttons.
function AdminMobileCard({ title, subtitle, badges = [], meta = [], actions = null, testid }) {
  return (
    <div data-testid={testid} className="glass rounded-2xl p-4 shimmer-border space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-base text-slate-900 truncate">{title}</div>
          {subtitle && <div className="text-xs font-mono text-slate-500 mt-0.5 break-all">{subtitle}</div>}
        </div>
        {badges.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5 shrink-0">
            {badges.filter(Boolean).map((b, i) => (
              <span
                key={i}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${b.tone || "bg-slate-100 border-slate-200 text-slate-700"}`}
              >
                {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
      {meta.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          {meta.filter(Boolean).map((m, i) => (
            <div key={i} className={m.wide ? "col-span-2" : ""}>
              <dt className="overline text-[9px]">{m.label}</dt>
              <dd className={`mt-0.5 text-slate-800 ${m.mono ? "font-mono" : ""} break-words`}>{m.value ?? "—"}</dd>
            </div>
          ))}
        </dl>
      )}
      {actions && (
        <div className="grid grid-cols-2 gap-2 pt-1">{actions}</div>
      )}
    </div>
  );
}

function AdminActionBtn({ tone = "amber", icon: Icon, children, ...props }) {
  const tones = {
    amber: "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200",
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-200",
    orange: "bg-orange-100 text-orange-900 border-orange-300 hover:bg-orange-200",
    sky: "bg-sky-100 text-sky-900 border-sky-300 hover:bg-sky-200",
    red: "bg-red-100 text-red-900 border-red-300 hover:bg-red-200",
    slate: "bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200",
  };
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-xl text-xs font-bold uppercase tracking-wider border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone] || tones.amber}`}
    >
      {Icon && <Icon size={14} weight="bold" />}
      {children}
    </button>
  );
}

/* --- USERS --- */
function UsersTab() {
  const [users, setUsers] = useState(null);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "requester", brands: [], currencies: [], payment_modes: [], is_approver: false, default_approver_email: "" });
  const [currencies, setCurrencies] = useState([]);
  const [approvers, setApprovers] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/brands").then((r) => setBrands(r.data.filter((b) => b.is_active))).catch(() => {});
    api.get("/currencies").then((r) => setCurrencies(r.data.filter((c) => c.is_active))).catch(() => {});
    api.get("/users/approvers-list").then((r) => setApprovers(r.data || [])).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(null); setForm({ email: "", name: "", password: "", role: "requester", brands: [], currencies: [], payment_modes: [], is_approver: false, default_approver_email: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, password: "", role: u.role, brands: u.brands || [], currencies: u.currencies || [], payment_modes: u.payment_modes || [], is_approver: !!u.is_approver, default_approver_email: u.default_approver_email || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) {
        const patch = { name: form.name, role: form.role, brands: form.brands, currencies: form.currencies, payment_modes: form.payment_modes, is_approver: form.is_approver, default_approver_email: form.default_approver_email || "" };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        const payload = { ...form, default_approver_email: form.default_approver_email || null };
        await api.post("/users", payload);
        toast.success("User created");
      }
      setOpen(false); load();
      // Refresh approvers list in case is_approver flag changed
      api.get("/users/approvers-list").then((r) => setApprovers(r.data || [])).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleBrand = (bid) => setForm((f) => ({ ...f, brands: f.brands.includes(bid) ? f.brands.filter((x) => x !== bid) : [...f.brands, bid] }));
  const toggleCurrency = (code) => setForm((f) => ({ ...f, currencies: f.currencies.includes(code) ? f.currencies.filter((x) => x !== code) : [...f.currencies, code] }));
  const togglePaymentMode = (mode) => setForm((f) => ({ ...f, payment_modes: f.payment_modes.includes(mode) ? f.payment_modes.filter((x) => x !== mode) : [...f.payment_modes, mode] }));

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.email}? They will need to set it up again next login.`)) return;
    try {
      await api.post(`/users/${u.id}/reset-2fa`);
      toast.success(`2FA reset for ${u.email}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const deactivate = async (u) => {
    if (!window.confirm(`Disable ${u.email}? They will no longer be able to log in.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success(`${u.email} disabled`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const activate = async (u) => {
    try { await api.post(`/users/${u.id}/activate`); toast.success(`${u.email} re-activated`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const hardDelete = async (u) => {
    // Fetch reference count first
    let refs = { total: 0, can_hard_delete: true };
    try {
      const r = await api.get(`/users/${u.id}/references`);
      refs = r.data;
    } catch (_) { /* fall through */ }

    if (!refs.can_hard_delete) {
      toast.error(`Cannot delete ${u.email} — referenced by ${refs.total} payment(s) ` +
        `(${refs.as_requester} as requester, ${refs.as_auditor} as auditor, ` +
        `${refs.as_payer} as payer, ${refs.as_verifier} as verifier). Disable instead.`,
        { duration: 8000 });
      return;
    }
    const confirmation = window.prompt(`Type DELETE to permanently remove ${u.email}. This cannot be undone.`);
    if (confirmation !== "DELETE") return;
    try {
      await api.delete(`/users/${u.id}?hard=true`);
      toast.success(`${u.email} deleted`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.message || JSON.stringify(detail)) : (detail || e.message);
      toast.error(msg, { duration: 8000 });
    }
  };

  if (users === null) return <TableSkeleton rows={5} />;

  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="text-sm text-slate-600">{users.length} users</div>
        <Button onClick={openNew} data-testid="admin-new-user" className="rounded-xl amber-pill h-10 px-4"><Plus size={16} className="mr-1.5" weight="bold" /> New user</Button>
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="users-mobile-list">
        {users.map((u) => (
          <AdminMobileCard
            key={u.id}
            testid={`user-card-${u.email}`}
            title={u.name}
            subtitle={u.email}
            badges={[
              { label: u.role, tone: "bg-amber-100 border-amber-300 text-amber-900" },
              u.is_active
                ? { label: "Active", tone: "bg-emerald-100 border-emerald-300 text-emerald-800" }
                : { label: "Disabled", tone: "bg-slate-200 border-slate-300 text-slate-700" },
              u.totp_enabled && { label: "2FA", tone: "bg-sky-100 border-sky-300 text-sky-800" },
              u.is_approver && { label: "Approver", tone: "bg-purple-100 border-purple-300 text-purple-800" },
            ]}
            meta={[
              { label: "Brands", value: (u.brands || []).length || "All" },
              u.role === "payer" && { label: "Currencies", value: (u.currencies || []).length || "All" },
              u.default_approver_email && { label: "Routes to", value: u.default_approver_email, mono: true, wide: true },
            ]}
            actions={
              <>
                <AdminActionBtn tone="amber" icon={User} onClick={() => openEdit(u)} data-testid={`user-edit-mob-${u.email}`}>Edit</AdminActionBtn>
                {u.totp_enabled && (
                  <AdminActionBtn tone="sky" onClick={() => reset2fa(u)} data-testid={`user-reset-2fa-mob-${u.email}`}>Reset 2FA</AdminActionBtn>
                )}
                {u.is_active ? (
                  <AdminActionBtn tone="orange" onClick={() => deactivate(u)} data-testid={`user-disable-mob-${u.email}`}>Disable</AdminActionBtn>
                ) : (
                  <AdminActionBtn tone="emerald" onClick={() => activate(u)} data-testid={`user-activate-mob-${u.email}`}>Activate</AdminActionBtn>
                )}
                <AdminActionBtn tone="red" icon={Trash} onClick={() => hardDelete(u)} data-testid={`user-delete-mob-${u.email}`}>Delete</AdminActionBtn>
              </>
            }
          />
        ))}
        {users.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No users yet.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="w-full ledger-table text-sm">
          <thead className="bg-white/40"><tr className="text-left">{["Email","Name","Role","Brands","2FA","Status","Actions"].map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 uppercase text-xs">{u.role}</td>
                <td className="px-4 py-3 text-xs">{(u.brands || []).length}</td>
                <td className="px-4 py-3 text-xs">{u.totp_enabled ? "ON" : "OFF"}</td>
                <td className="px-4 py-3 text-xs">{u.is_active ? "Active" : "Disabled"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.email}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50">Edit</button>
                    {u.totp_enabled && (
                      <button onClick={() => reset2fa(u)} data-testid={`user-reset-2fa-${u.email}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-sky-700 hover:bg-sky-50">Reset 2FA</button>
                    )}
                    {u.is_active ? (
                      <button onClick={() => deactivate(u)} data-testid={`user-disable-${u.email}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-orange-700 hover:bg-orange-50">Disable</button>
                    ) : (
                      <button onClick={() => activate(u)} data-testid={`user-activate-${u.email}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-emerald-700 hover:bg-emerald-50">Activate</button>
                    )}
                    <button onClick={() => hardDelete(u)} data-testid={`user-delete-${u.email}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-red-700 hover:bg-red-50">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Email</Label><Input data-testid="user-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} /></div>
            <div><Label>Name</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label><Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>{["admin","requester","auditor","payer","approver"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned brands (empty = all for admin)</Label>
              <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-auto border border-border rounded-lg p-2 bg-white/50">
                {brands.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`user-form-brand-${b.code}`} checked={form.brands.includes(b.id)} onCheckedChange={() => toggleBrand(b.id)} /> {b.name}
                  </label>
                ))}
              </div>
            </div>

            {form.role === "payer" && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-3">
                <div className="text-xs font-semibold tracking-wide text-amber-900">
                  PAYER SCOPE — restrict this user to specific currencies and payment modes.
                  Leave empty = access to all.
                </div>
                <div>
                  <Label className="text-xs">Allowed currencies</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-32 overflow-auto border border-amber-200 rounded-lg p-2 bg-white/60">
                    {currencies.map((c) => (
                      <label key={c.code} className="flex items-center gap-2 text-sm">
                        <Checkbox data-testid={`user-form-curr-${c.code}`} checked={form.currencies.includes(c.code)} onCheckedChange={() => toggleCurrency(c.code)} />
                        <span className="font-mono text-xs">{c.code}</span> <span className="text-slate-600 text-xs truncate">— {c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Allowed payment modes</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["bank", "cash", "credit_card", "crypto"].map((m) => (
                      <label key={m} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-200 bg-white/70 text-xs cursor-pointer">
                        <Checkbox data-testid={`user-form-mode-${m}`} checked={form.payment_modes.includes(m)} onCheckedChange={() => togglePaymentMode(m)} />
                        <span className="uppercase font-semibold">{m}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-[11px] text-amber-800 italic">
                  Example: brands=[BrandA], currencies=[USDT], payment_modes=[crypto] → this payer only sees BrandA USDT crypto payouts.
                </div>
              </div>
            )}

            {/* Approver settings — applies to any role */}
            <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-3 space-y-3">
              <div className="text-xs font-heading font-bold uppercase tracking-wider text-purple-900">Approver workflow</div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  data-testid="user-form-is-approver"
                  checked={form.is_approver}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_approver: !!v }))}
                />
                <span>
                  <span className="font-semibold">This user is an approver</span>
                  <span className="block text-[11px] text-slate-600 mt-0.5">Other users can be routed to them for pre-audit approval.</span>
                </span>
              </label>
              <div>
                <Label className="text-xs">Route this user&apos;s requests to (optional)</Label>
                <Select
                  value={form.default_approver_email || "__none__"}
                  onValueChange={(v) => setForm((f) => ({ ...f, default_approver_email: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger data-testid="user-form-default-approver" className="mt-1">
                    <SelectValue placeholder="No default approver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None (no pre-audit approval) —</SelectItem>
                    {approvers
                      .filter((a) => !editing || a.email !== editing.email) // can't self-approve
                      .map((a) => (
                        <SelectItem key={a.id} value={a.email}>
                          {a.name} · {a.email} ({a.role})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-600 mt-1">Every payment request this user creates will go to the selected approver before hitting the audit queue.</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} data-testid="user-form-save" className="rounded-xl amber-pill">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --- BRANDS --- */
function BrandsTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", description: "" });
  const load = useCallback(async () => { const r = await api.get("/brands"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing(null); setForm({ name: "", code: "", description: "" }); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, code: b.code, description: b.description || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) { await api.patch(`/brands/${editing.id}`, form); toast.success("Brand updated"); }
      else { await api.post("/brands", form); toast.success("Brand created"); }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (b) => {
    try {
      if (b.is_active) { await api.delete(`/brands/${b.id}`); toast.success("Brand disabled"); }
      else { await api.patch(`/brands/${b.id}`, { name: b.name, code: b.code, description: b.description, is_active: true }); toast.success("Brand enabled"); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (items === null) return <TableSkeleton rows={3} />;
  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="text-sm text-slate-600">{items.length} brands</div>
        <Button onClick={openNew} data-testid="admin-new-brand" className="rounded-xl amber-pill h-10 px-4"><Plus size={16} className="mr-1.5" weight="bold" /> New brand</Button>
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="brands-mobile-list">
        {items.map((b) => (
          <AdminMobileCard
            key={b.id}
            testid={`brand-card-${b.code}`}
            title={b.name}
            subtitle={b.code}
            badges={[
              b.is_active
                ? { label: "Active", tone: "bg-emerald-100 border-emerald-300 text-emerald-800" }
                : { label: "Disabled", tone: "bg-slate-200 border-slate-300 text-slate-700" },
            ]}
            meta={[
              b.description && { label: "Description", value: b.description, wide: true },
            ]}
            actions={
              <>
                <AdminActionBtn tone="amber" onClick={() => openEdit(b)} data-testid={`brand-edit-mob-${b.code}`}>Edit</AdminActionBtn>
                {b.is_active ? (
                  <AdminActionBtn tone="orange" onClick={() => toggleActive(b)} data-testid={`brand-toggle-mob-${b.code}`}>Disable</AdminActionBtn>
                ) : (
                  <AdminActionBtn tone="emerald" onClick={() => toggleActive(b)} data-testid={`brand-toggle-mob-${b.code}`}>Enable</AdminActionBtn>
                )}
              </>
            }
          />
        ))}
        {items.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No brands yet.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="w-full ledger-table text-sm">
          <thead className="bg-white/40"><tr className="text-left">{["Name","Code","Description","Active","Actions"].map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
          <tbody>{items.map((b) => (
            <tr key={b.id} data-testid={`brand-row-${b.code}`}>
              <td className="px-4 py-3">{b.name}</td><td className="px-4 py-3 font-mono text-xs">{b.code}</td>
              <td className="px-4 py-3 text-xs text-slate-600">{b.description || "—"}</td>
              <td className="px-4 py-3 text-xs">{b.is_active ? "YES" : "NO"}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => openEdit(b)} data-testid={`brand-edit-${b.code}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50">Edit</button>
                <button onClick={() => toggleActive(b)} data-testid={`brand-toggle-${b.code}`} className={`px-2 py-1 rounded-lg text-xs font-semibold ${b.is_active ? "text-orange-700 hover:bg-orange-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{b.is_active ? "Disable" : "Enable"}</button>
              </td>
            </tr>
          ))}{items.length === 0 && (<tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">No brands yet.</td></tr>)}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit brand · ${editing.name}` : "New brand"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="brand-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Code</Label><Input data-testid="brand-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
            <div><Label>Description</Label><Input data-testid="brand-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="brand-form-save" className="rounded-xl amber-pill">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --- CURRENCIES --- */
function CurrenciesTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { code: "", name: "", symbol: "", is_crypto: false, networks: "", decimals: 2, charge_percent: 0 };
  const [form, setForm] = useState(emptyForm);
  const load = useCallback(async () => { const r = await api.get("/currencies"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ code: c.code, name: c.name, symbol: c.symbol || "", is_crypto: c.is_crypto, networks: (c.networks || []).join(", "), decimals: c.decimals ?? 2, charge_percent: c.charge_percent ?? 0 }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, networks: form.networks.split(",").map((s) => s.trim()).filter(Boolean), decimals: Number(form.decimals), charge_percent: Number(form.charge_percent || 0) };
      if (editing) { await api.patch(`/currencies/${editing.id}`, payload); toast.success("Currency updated"); }
      else { await api.post("/currencies", payload); toast.success("Currency added"); }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (c) => {
    try {
      if (c.is_active) { await api.delete(`/currencies/${c.id}`); toast.success("Currency disabled"); }
      else {
        const payload = { code: c.code, name: c.name, symbol: c.symbol || "", is_crypto: c.is_crypto, networks: c.networks || [], decimals: c.decimals ?? 2, charge_percent: c.charge_percent ?? 0 };
        await api.patch(`/currencies/${c.id}`, payload); toast.success("Currency enabled");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (items === null) return <TableSkeleton rows={5} />;
  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="text-sm text-slate-600">{items.length} currencies</div>
        <Button onClick={openNew} data-testid="admin-new-currency" className="rounded-xl amber-pill h-10 px-4"><Plus size={16} className="mr-1.5" weight="bold" /> New currency</Button>
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="currencies-mobile-list">
        {items.map((c) => (
          <div key={c.id} data-testid={`currency-card-${c.code}`} className="glass rounded-2xl p-4 shimmer-border space-y-3">
            <div className="flex items-start gap-3">
              <CurrencyIcon code={c.code} size={32} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-heading font-black text-lg font-mono">{c.code}</div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${c.is_crypto ? "bg-purple-100 border-purple-300 text-purple-800" : "bg-amber-100 border-amber-300 text-amber-800"}`}>
                    {c.is_crypto ? "Crypto" : "Fiat"}
                  </span>
                  {c.is_active ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-emerald-100 border-emerald-300 text-emerald-800">Active</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-slate-200 border-slate-300 text-slate-700">Disabled</span>
                  )}
                </div>
                <div className="text-sm text-slate-700 mt-0.5 truncate">{c.name}</div>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <dt className="overline text-[9px]">Charge</dt>
                <dd className="font-mono font-bold text-amber-700 mt-0.5">{(c.charge_percent || 0).toFixed(2)}%</dd>
              </div>
              {c.is_crypto && (
                <div>
                  <dt className="overline text-[9px]">Networks</dt>
                  <dd className="font-mono text-xs mt-0.5 break-all">{(c.networks || []).join(", ") || "—"}</dd>
                </div>
              )}
            </dl>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <AdminActionBtn tone="amber" onClick={() => openEdit(c)} data-testid={`currency-edit-mob-${c.code}`}>Edit</AdminActionBtn>
              {c.is_active ? (
                <AdminActionBtn tone="orange" onClick={() => toggleActive(c)} data-testid={`currency-toggle-mob-${c.code}`}>Disable</AdminActionBtn>
              ) : (
                <AdminActionBtn tone="emerald" onClick={() => toggleActive(c)} data-testid={`currency-toggle-mob-${c.code}`}>Enable</AdminActionBtn>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="w-full ledger-table text-sm">
          <thead className="bg-white/40"><tr className="text-left">{["","Code","Name","Type","Networks","Charge %","Active","Actions"].map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.id} data-testid={`currency-row-${c.code}`}>
              <td className="px-4 py-3"><CurrencyIcon code={c.code} size={24} /></td>
              <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
              <td className="px-4 py-3">{c.name}</td>
              <td className="px-4 py-3 text-xs uppercase">{c.is_crypto ? "Crypto" : "Fiat"}</td>
              <td className="px-4 py-3 text-xs font-mono">{(c.networks || []).join(", ") || "—"}</td>
              <td className="px-4 py-3 font-mono font-semibold">{(c.charge_percent || 0).toFixed(2)}%</td>
              <td className="px-4 py-3 text-xs">{c.is_active ? "YES" : "NO"}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => openEdit(c)} data-testid={`currency-edit-${c.code}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50">Edit</button>
                <button onClick={() => toggleActive(c)} data-testid={`currency-toggle-${c.code}`} className={`px-2 py-1 rounded-lg text-xs font-semibold ${c.is_active ? "text-orange-700 hover:bg-orange-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{c.is_active ? "Disable" : "Enable"}</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit currency · ${editing.code}` : "New currency"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Code (e.g. USD, USDT)</Label><Input data-testid="curr-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editing} /></div>
            <div><Label>Name</Label><Input data-testid="curr-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Symbol (optional — used for fiat)</Label><Input data-testid="curr-form-symbol" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch data-testid="curr-form-crypto" checked={form.is_crypto} onCheckedChange={(v) => setForm({ ...form, is_crypto: v })} /> <Label>Cryptocurrency</Label></div>
            {form.is_crypto && (<div><Label>Networks (comma-separated)</Label><Input data-testid="curr-form-networks" value={form.networks} onChange={(e) => setForm({ ...form, networks: e.target.value })} placeholder="TRC20, ERC20, BEP20" /></div>)}
            <div><Label>Decimals</Label><Input data-testid="curr-form-decimals" type="number" value={form.decimals} onChange={(e) => setForm({ ...form, decimals: e.target.value })} /></div>
            <div>
              <Label>Charge % on top (e.g. 1 = 1% bank fee added to amount)</Label>
              <Input data-testid="curr-form-charge" type="number" step="0.01" value={form.charge_percent} onChange={(e) => setForm({ ...form, charge_percent: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">Payer transfers = amount × (1 + charge% / 100). Set 0 for no charge.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="curr-form-save" className="rounded-xl amber-pill">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --- EXPENSE TYPES --- */

/* --- PAYER VENDORS (bank processors used by the payer) --- */
function PayerVendorsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", charge_percent: 0, notes: "" });
  const load = useCallback(async () => {
    const url = isAdmin ? "/payer-vendors/all" : "/payer-vendors";
    const r = await api.get(url);
    setItems(r.data);
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing(null); setForm({ name: "", charge_percent: 0, notes: "" }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name: v.name, charge_percent: v.charge_percent, notes: v.notes || "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, charge_percent: Number(form.charge_percent) || 0 };
      if (editing) { await api.patch(`/payer-vendors/${editing.id}`, payload); toast.success("Payer vendor updated"); }
      else { await api.post("/payer-vendors", payload); toast.success("Payer vendor created"); }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (v) => {
    try {
      if (v.is_active) { await api.delete(`/payer-vendors/${v.id}`); toast.success("Payer vendor disabled"); }
      else { await api.patch(`/payer-vendors/${v.id}`, { is_active: true }); toast.success("Payer vendor enabled"); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const hardDelete = async (v) => {
    if (!window.confirm(`Permanently delete "${v.name}"?`)) return;
    try { await api.delete(`/payer-vendors/${v.id}?hard=true`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (items === null) return <TableSkeleton rows={4} />;
  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="text-sm text-slate-600">{items.length} payer vendor{items.length === 1 ? "" : "s"} · used to fulfil bank payments</div>
        {isAdmin && <Button onClick={openNew} data-testid="admin-new-payer-vendor" className="rounded-xl amber-pill h-10 px-4"><Plus size={16} className="mr-1.5" weight="bold" /> New payer vendor</Button>}
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="payer-vendors-mobile-list">
        {items.map((v) => (
          <AdminMobileCard
            key={v.id}
            testid={`payer-vendor-card-${v.name}`}
            title={v.name}
            badges={[
              { label: `${v.charge_percent}%`, tone: "bg-amber-100 border-amber-300 text-amber-900" },
              isAdmin && (v.is_active
                ? { label: "Active", tone: "bg-emerald-100 border-emerald-300 text-emerald-800" }
                : { label: "Disabled", tone: "bg-slate-200 border-slate-300 text-slate-700" }),
            ]}
            meta={[
              v.notes && { label: "Notes", value: v.notes, wide: true },
            ]}
            actions={isAdmin ? (
              <>
                <AdminActionBtn tone="amber" onClick={() => openEdit(v)} data-testid={`payer-vendor-edit-mob-${v.name}`}>Edit</AdminActionBtn>
                {v.is_active ? (
                  <AdminActionBtn tone="orange" onClick={() => toggleActive(v)} data-testid={`payer-vendor-toggle-mob-${v.name}`}>Disable</AdminActionBtn>
                ) : (
                  <AdminActionBtn tone="emerald" onClick={() => toggleActive(v)} data-testid={`payer-vendor-toggle-mob-${v.name}`}>Enable</AdminActionBtn>
                )}
                <AdminActionBtn tone="red" icon={Trash} onClick={() => hardDelete(v)} data-testid={`payer-vendor-delete-mob-${v.name}`}>Delete</AdminActionBtn>
              </>
            ) : null}
          />
        ))}
        {items.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No payer vendors yet. Add PayPal, Wise, your bank, etc.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="w-full ledger-table text-sm">
          <thead className="bg-white/40"><tr className="text-left">{(isAdmin ? ["Name","Charge %","Notes","Active","Actions"] : ["Name","Charge %","Notes"]).map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
          <tbody>{items.map((v) => (
            <tr key={v.id} data-testid={`payer-vendor-row-${v.name}`}>
              <td className="px-4 py-3 font-medium">{v.name}</td>
              <td className="px-4 py-3 font-mono">{v.charge_percent}%</td>
              <td className="px-4 py-3 text-xs text-slate-600">{v.notes || "—"}</td>
              {isAdmin && <td className="px-4 py-3 text-xs">{v.is_active ? "YES" : "NO"}</td>}
              {isAdmin && (
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(v)} data-testid={`payer-vendor-edit-${v.name}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50">Edit</button>
                  <button onClick={() => toggleActive(v)} data-testid={`payer-vendor-toggle-${v.name}`} className={`px-2 py-1 rounded-lg text-xs font-semibold ${v.is_active ? "text-orange-700 hover:bg-orange-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{v.is_active ? "Disable" : "Enable"}</button>
                  <button onClick={() => hardDelete(v)} data-testid={`payer-vendor-delete-${v.name}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><Trash size={12} weight="bold" /> Delete</button>
                </td>
              )}
            </tr>
          ))}{items.length === 0 && (<tr><td colSpan={isAdmin ? 5 : 3} className="px-4 py-8 text-center text-sm text-slate-500">No payer vendors yet. Add PayPal, Wise, your bank, etc.</td></tr>)}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit · ${editing.name}` : "New payer vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="payer-vendor-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PayPal, Wise, Chase Business…" /></div>
            <div><Label>Charge %</Label><Input data-testid="payer-vendor-form-pct" type="number" step="any" min="0" value={form.charge_percent} onChange={(e) => setForm({ ...form, charge_percent: e.target.value })} className="font-mono" /></div>
            <div><Label>Notes</Label><Input data-testid="payer-vendor-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional context" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="payer-vendor-form-save" className="rounded-xl amber-pill">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpenseTypesTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const load = useCallback(async () => {
    const url = isAdmin ? "/expense-types/all" : "/expense-types";
    const r = await api.get(url);
    setItems(r.data);
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing(null); setForm({ name: "", description: "" }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, description: t.description || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) { await api.patch(`/expense-types/${editing.id}`, form); toast.success("Type updated"); }
      else { await api.post("/expense-types", form); toast.success("Expense type created"); }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (t) => {
    try {
      if (t.is_active) { await api.delete(`/expense-types/${t.id}`); toast.success("Type disabled"); }
      else { await api.patch(`/expense-types/${t.id}`, { is_active: true }); toast.success("Type enabled"); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const hardDelete = async (t) => {
    if (!window.confirm(`Permanently delete "${t.name}"? This cannot be undone. If any payment references this type, deletion will be blocked.`)) return;
    try {
      await api.delete(`/expense-types/${t.id}?hard=true`);
      toast.success("Expense type deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (items === null) return <TableSkeleton rows={4} />;
  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="text-sm text-slate-600">{items.length} expense types</div>
        <Button onClick={openNew} data-testid="admin-new-expense-type" className="rounded-xl amber-pill h-10 px-4"><Plus size={16} className="mr-1.5" weight="bold" /> New type</Button>
      </div>
      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="expense-types-mobile-list">
        {items.map((t) => (
          <AdminMobileCard
            key={t.id}
            testid={`expense-type-card-${t.name}`}
            title={t.name}
            badges={[
              isAdmin && (t.is_active
                ? { label: "Active", tone: "bg-emerald-100 border-emerald-300 text-emerald-800" }
                : { label: "Disabled", tone: "bg-slate-200 border-slate-300 text-slate-700" }),
            ]}
            meta={[
              t.description && { label: "Description", value: t.description, wide: true },
            ]}
            actions={isAdmin ? (
              <>
                <AdminActionBtn tone="amber" onClick={() => openEdit(t)} data-testid={`expense-type-edit-mob-${t.name}`}>Edit</AdminActionBtn>
                {t.is_active ? (
                  <AdminActionBtn tone="orange" onClick={() => toggleActive(t)} data-testid={`expense-type-toggle-mob-${t.name}`}>Disable</AdminActionBtn>
                ) : (
                  <AdminActionBtn tone="emerald" onClick={() => toggleActive(t)} data-testid={`expense-type-toggle-mob-${t.name}`}>Enable</AdminActionBtn>
                )}
                <AdminActionBtn tone="red" icon={Trash} onClick={() => hardDelete(t)} data-testid={`expense-type-delete-mob-${t.name}`}>Delete</AdminActionBtn>
              </>
            ) : null}
          />
        ))}
        {items.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No expense types yet.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="w-full ledger-table text-sm">
          <thead className="bg-white/40"><tr className="text-left">{(isAdmin ? ["Name","Description","Active","Actions"] : ["Name","Description"]).map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
          <tbody>{items.map((t) => (
            <tr key={t.id} data-testid={`expense-type-row-${t.name}`}>
              <td className="px-4 py-3 font-medium">{t.name}</td>
              <td className="px-4 py-3 text-xs text-slate-600">{t.description || "—"}</td>
              {isAdmin && <td className="px-4 py-3 text-xs">{t.is_active ? "YES" : "NO"}</td>}
              {isAdmin && (
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(t)} data-testid={`expense-type-edit-${t.name}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50">Edit</button>
                  <button onClick={() => toggleActive(t)} data-testid={`expense-type-toggle-${t.name}`} className={`px-2 py-1 rounded-lg text-xs font-semibold ${t.is_active ? "text-orange-700 hover:bg-orange-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{t.is_active ? "Disable" : "Enable"}</button>
                  <button onClick={() => hardDelete(t)} data-testid={`expense-type-delete-${t.name}`} className="px-2 py-1 rounded-lg text-xs font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><Trash size={12} weight="bold" /> Delete</button>
                </td>
              )}
            </tr>
          ))}{items.length === 0 && (<tr><td colSpan={isAdmin ? 4 : 2} className="px-4 py-8 text-center text-sm text-slate-500">No expense types yet.</td></tr>)}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit type · ${editing.name}` : "New expense type"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="expense-type-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Influencer Marketing" /></div>
            <div><Label>Description</Label><Input data-testid="expense-type-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="expense-type-form-save" className="rounded-xl amber-pill">{editing ? "Save changes" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* --- SECURITY --- */
function SecurityTab() {
  const { settings, setSettings } = useAuth();
  const [s, setS] = useState(settings);
  const [ipInput, setIpInput] = useState("");
  useEffect(() => { setS(settings); }, [settings]);
  useEffect(() => {
    // Fetch full settings including the global IP list
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const save = async (patch) => {
    try {
      const { data } = await api.patch("/settings", patch);
      setS(data);
      setSettings((prev) => ({ ...prev, totp_enforced: data.totp_enforced, ip_whitelist_enforced: data.ip_whitelist_enforced }));
      toast.success("Settings updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const ips = s.global_ip_whitelist || [];
  const addIp = () => {
    const v = ipInput.trim();
    if (!v) return;
    if (ips.includes(v)) { setIpInput(""); return; }
    save({ global_ip_whitelist: [...ips, v] });
    setIpInput("");
  };
  const removeIp = (ip) => {
    save({ global_ip_whitelist: ips.filter((x) => x !== ip) });
  };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="glass rounded-2xl p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-heading font-bold text-lg">Enforce 2FA (TOTP)</div>
            <p className="text-sm text-slate-600 mt-1">
              When on, every user must complete a 2FA setup on first login (or their next login).
              Users without 2FA are blocked from the app until they finish enrollment.
              Currently: <span className="font-mono font-semibold">{s.totp_enforced ? "ENFORCED" : "OPTIONAL"}</span>
            </p>
          </div>
          <Switch data-testid="settings-totp" checked={!!s.totp_enforced} onCheckedChange={(v) => save({ totp_enforced: v })} />
        </div>
      </div>

      <div className="glass rounded-2xl p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-heading font-bold text-lg">Enforce IP whitelist</div>
            <p className="text-sm text-slate-600 mt-1">
              When on, logins are only accepted from IPs in the global list below.
              An <b>empty list is a safety fallback</b> — no one is blocked so an admin can&apos;t self-lock the app.
              The seeded super-admin (<span className="font-mono">steve@brightertails.com</span>) always bypasses this check as a break-glass safety.
              Currently: <span className="font-mono font-semibold">{s.ip_whitelist_enforced ? "ENFORCED" : "OFF"}</span>
            </p>
          </div>
          <Switch data-testid="settings-ip" checked={!!s.ip_whitelist_enforced} onCheckedChange={(v) => save({ ip_whitelist_enforced: v })} />
        </div>

        <div className="mt-5 pt-5 border-t border-amber-100">
          <div className="text-xs font-semibold tracking-wide text-slate-700 mb-2">GLOBAL ALLOWED IPS</div>
          <div className="flex gap-2 mb-3">
            <Input
              data-testid="settings-ip-input"
              value={ipInput}
              onChange={(e) => setIpInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIp(); } }}
              placeholder="e.g. 203.0.113.5 or 2001:db8::1"
              className="font-mono text-xs flex-1"
            />
            <Button data-testid="settings-ip-add" onClick={addIp} className="rounded-xl amber-pill h-10 px-4">Add IP</Button>
          </div>
          {ips.length === 0 && (
            <div className="text-xs text-slate-500 italic">No IPs added yet — whitelist is inactive (fallback).</div>
          )}
          <div className="flex flex-wrap gap-2">
            {ips.map((ip) => (
              <span key={ip} data-testid={`settings-ip-pill-${ip}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-amber-200 text-xs font-mono">
                {ip}
                <button onClick={() => removeIp(ip)} className="text-red-600 hover:text-red-800 font-bold" aria-label={`remove ${ip}`}>×</button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- DEMO MODE --- */
function DemoTab() {
  const { logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [seeded, setSeeded] = useState(null); // result of last seed with credentials
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/demo/status");
    setStatus(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo/seed");
      setSeeded(data);
      toast.success("Demo data seeded");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove all demo brands, users and payments? Real data is untouched.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/demo/clear");
      toast.success(`Removed ${data.payments_removed} payments, ${data.brands_removed} brands, ${data.users_removed} users`);
      setSeeded(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const loginAs = async (email) => {
    await logout();
    // Store hint for Login page to pre-fill
    localStorage.setItem("prefill_email", email);
    window.location.href = "/login";
  };

  const hasDemo = status && (status.users > 0 || status.payments > 0);

  return (
    <div className="max-w-3xl space-y-6">
      <div className="glass rounded-2xl p-4 sm:p-6 shimmer-border">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl chrome flex items-center justify-center">
            <Sparkle size={22} weight="fill" className="text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-lg">Demo mode</div>
            <p className="text-sm text-slate-600 mt-1">
              Seed realistic sample data — 3 brands, 3 users (requester / auditor / payer), and ~24 payments across every stage — so you can log in as each role and see how the app looks from their view. Everything is tagged and can be cleared in one click. Your real data is never touched.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          <StatMini label="Demo users" value={status?.users ?? "—"} />
          <StatMini label="Demo brands" value={status?.brands ?? "—"} />
          <StatMini label="Demo payments" value={status?.payments ?? "—"} />
        </div>

        <div className="mt-5 flex gap-3">
          <Button data-testid="demo-seed" onClick={seed} disabled={busy} className="amber-pill rounded-xl h-11 px-5">
            <Sparkle size={16} weight="fill" className="mr-2" /> Seed demo data
          </Button>
          <Button data-testid="demo-clear" onClick={clear} disabled={busy || !hasDemo} variant="outline" className="rounded-xl h-11 px-5 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40">
            <Trash size={16} weight="bold" className="mr-2" /> Clear demo data
          </Button>
        </div>
      </div>

      {(seeded || hasDemo) && (
        <div className="glass rounded-2xl p-4 sm:p-6">
          <div className="overline mb-3">Demo user credentials</div>
          <p className="text-sm text-slate-600 mb-4">Click a role to log out and jump to the login screen prefilled with that user. Password for all demo accounts: <code className="font-mono text-xs bg-white/70 px-2 py-0.5 rounded">Demo@123</code></p>
          <div className="space-y-2">
            {(seeded?.users || [
              {email: "demo.requester@paytrack.dev", role: "requester", password: "Demo@123"},
              {email: "demo.auditor@paytrack.dev",   role: "auditor",   password: "Demo@123"},
              {email: "demo.payer@paytrack.dev",     role: "payer",     password: "Demo@123"},
            ]).map((u) => (
              <div key={u.email} data-testid={`demo-user-${u.role}`} className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-amber-100">
                <div className="w-9 h-9 rounded-full chrome flex items-center justify-center">
                  <User size={16} weight="duotone" className="text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold uppercase tracking-wide text-slate-700">{u.role}</div>
                  <div className="font-mono text-xs text-slate-600">{u.email}</div>
                </div>
                <button onClick={() => copy(u.email)} className="p-2 hover:bg-amber-50 rounded-lg" title="Copy email">
                  <Copy size={14} />
                </button>
                <Button data-testid={`demo-login-${u.role}`} size="sm" onClick={() => loginAs(u.email)} className="amber-pill rounded-lg h-9">
                  Log in as {u.role}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="rounded-xl bg-white/60 border border-amber-100 p-3">
      <div className="overline">{label}</div>
      <div className="font-heading font-black text-2xl text-amber-700 mt-1">{value}</div>
    </div>
  );
}

/* --- TOTP --- */
function TotpTab() {
  const { user, refresh } = useAuth();
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  // Password change state
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const setup = async () => {
    const { data } = await api.post("/auth/totp/setup");
    setQr(data.qr_data_url); setSecret(data.secret);
  };
  const verify = async () => {
    try {
      await api.post("/auth/totp/verify", { code });
      toast.success("2FA enabled");
      setQr(null); setSecret(null); setCode("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const disable = async () => {
    try {
      await api.post("/auth/totp/disable", { code });
      toast.success("2FA disabled"); setCode(""); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changePassword = async () => {
    if (!pw.current || !pw.next) { toast.error("Fill both password fields"); return; }
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New password and confirmation don't match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setPwBusy(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div className="glass rounded-2xl p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <ShieldCheck size={28} weight="duotone" className="text-amber-700" />
          <div className="flex-1">
            <div className="font-heading font-bold text-lg">Two-factor authentication</div>
            <p className="text-sm text-slate-600 mt-1">Status: <span className="font-mono">{user?.totp_enabled ? "ENABLED" : "DISABLED"}</span></p>
          </div>
        </div>

        {!user?.totp_enabled && !qr && (
          <Button data-testid="totp-setup-btn" onClick={setup} className="mt-4 rounded-xl amber-pill"><QrCode size={16} className="mr-2" weight="bold" /> Set up 2FA</Button>
        )}

        {qr && (
          <div className="mt-6 space-y-3">
            <p className="text-sm">1) Scan this QR with Google Authenticator</p>
            <img src={qr} alt="qr" className="w-48 h-48 border border-border rounded-lg" data-testid="totp-qr" />
            <p className="text-xs text-slate-600">Or enter secret manually: <code className="font-mono">{secret}</code></p>
            <div>
              <Label>2) Enter 6-digit code</Label>
              <Input data-testid="totp-verify-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric" />
            </div>
            <Button data-testid="totp-verify-btn" onClick={verify} className="rounded-xl amber-pill">Verify & enable</Button>
          </div>
        )}

        {user?.totp_enabled && (
          <div className="mt-6 space-y-3 border-t border-border pt-4">
            <p className="text-sm">Disable 2FA (requires current code):</p>
            <Input data-testid="totp-disable-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric" placeholder="123456" />
            <Button data-testid="totp-disable-btn" onClick={disable} variant="outline" className="rounded-xl border-red-600 text-red-700 hover:bg-red-50">Disable 2FA</Button>
          </div>
        )}
      </div>

      {/* Change password */}
      <div className="glass rounded-2xl p-4 sm:p-6" data-testid="change-password-card">
        <div className="flex items-start gap-4">
          <ShieldCheck size={28} weight="duotone" className="text-amber-700" />
          <div className="flex-1">
            <div className="font-heading font-bold text-lg">Change password</div>
            <p className="text-sm text-slate-600 mt-1">Update your sign-in password. You&apos;ll stay logged in on this session.</p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <Label>Current password</Label>
            <Input data-testid="pw-current" type="password" autoComplete="current-password"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
          </div>
          <div>
            <Label>New password (min 6 characters)</Label>
            <Input data-testid="pw-new" type="password" autoComplete="new-password"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input data-testid="pw-confirm" type="password" autoComplete="new-password"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          </div>
          <Button
            data-testid="pw-submit"
            onClick={changePassword}
            disabled={pwBusy}
            className="rounded-xl amber-pill"
          >
            {pwBusy ? "Updating…" : "Change password"}
          </Button>
        </div>
      </div>

      <PushNotificationsCard />
    </div>
  );
}


function PushNotificationsCard() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (typeof window === "undefined") return;
    const canUse =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(canUse);
    if (!canUse) return;
    setPermission(Notification.permission);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const enable = async () => {
    setBusy(true);
    try {
      // Clear the "already prompted" flag so this button forces a fresh prompt.
      localStorage.removeItem("push_permission_prompted");
      const result = await ensurePushSubscription({ prompt: true });
      if (!result.ok) {
        const map = {
          unsupported: "This browser doesn't support push notifications.",
          "no-sw": "Service worker unavailable — try reloading the page.",
          denied: "Permission denied. Enable notifications in your browser/OS settings.",
          "server-key-unavailable": `Push is not configured on this server. ${result.detail || ""}`,
          "server-key-empty": "Push not initialized on this server. Ask an admin to restart the backend.",
          "server-key-malformed": `Server returned an invalid VAPID key. ${result.detail || ""}`,
          "server-key-decode-failed": "Server returned a VAPID key we couldn't decode.",
          "subscribe-failed": `Browser rejected the subscription: ${result.detail || "unknown error"}. Try in Chrome/Edge or reinstall the PWA.`,
        };
        toast.error(map[result.reason] || `Couldn't enable push (${result.reason}).`);
      } else {
        toast.success("Push notifications enabled on this device.");
      }
    } catch (e) {
      toast.error(e.message || "Failed to enable push");
    } finally {
      await refreshStatus();
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push disabled on this device.");
    } catch (e) {
      toast.error(e.message || "Failed to disable");
    } finally {
      await refreshStatus();
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test");
      const n = r.data?.sent ?? 0;
      if (n > 0) {
        toast.success(`Test sent to ${n} device${n === 1 ? "" : "s"}. Check your notifications.`);
      } else {
        toast.warning("No active subscriptions on the server. Try 'Enable push' first.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to send test");
    } finally {
      setBusy(false);
    }
  };

  const statusPill = () => {
    if (!supported) {
      return { label: "Unsupported", tone: "bg-slate-100 text-slate-700 border-slate-300", icon: BellSlash };
    }
    if (permission === "denied") {
      return { label: "Blocked", tone: "bg-red-100 text-red-800 border-red-300", icon: BellSlash };
    }
    if (subscribed && permission === "granted") {
      return { label: "Enabled", tone: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: BellRinging };
    }
    return { label: "Off", tone: "bg-amber-100 text-amber-800 border-amber-300", icon: Bell };
  };
  const st = statusPill();
  const Icon = st.icon;

  return (
    <div className="glass rounded-2xl p-4 sm:p-6" data-testid="push-card">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl chrome flex items-center justify-center shrink-0">
          <BellRinging size={20} weight="duotone" className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-lg">Push notifications</div>
          <p className="text-sm text-slate-600 mt-1">
            Get instant alerts on this device when a payment moves to your queue.
            Works best inside the installed PayTrack PWA.
          </p>
          <div className="mt-3">
            <span
              data-testid="push-status-pill"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${st.tone}`}
            >
              <Icon size={12} weight="fill" /> {st.label}
            </span>
          </div>
        </div>
      </div>

      {!supported && (
        <p className="mt-4 text-xs text-slate-600 leading-relaxed">
          Your browser doesn&apos;t expose the Web Push API. On iPhone, install the PWA
          via Safari &rarr; Share &rarr; &ldquo;Add to Home Screen&rdquo;, then open it
          from the home screen icon to enable notifications.
        </p>
      )}

      {supported && permission === "denied" && (
        <p className="mt-4 text-xs text-red-700 leading-relaxed">
          Notifications are blocked for this site. Open your browser or OS settings and
          allow notifications for PayTrack, then reload this page.
        </p>
      )}

      {supported && permission !== "denied" && (
        <div className="mt-5 flex flex-wrap gap-3">
          {!subscribed ? (
            <Button
              data-testid="push-enable-btn"
              onClick={enable}
              disabled={busy}
              className="amber-pill rounded-xl h-11 px-5"
            >
              <BellRinging size={16} weight="bold" className="mr-2" />
              {busy ? "Enabling…" : "Enable push on this device"}
            </Button>
          ) : (
            <Button
              data-testid="push-disable-btn"
              onClick={disable}
              disabled={busy}
              variant="outline"
              className="rounded-xl h-11 px-5 border-slate-300 hover:bg-slate-100"
            >
              <BellSlash size={16} weight="bold" className="mr-2" /> Disable on this device
            </Button>
          )}
          <Button
            data-testid="push-test-btn"
            onClick={sendTest}
            disabled={busy}
            variant="outline"
            className="rounded-xl h-11 px-5 border-amber-300 text-amber-800 hover:bg-amber-50"
          >
            <Bell size={16} weight="bold" className="mr-2" />
            {busy ? "Sending…" : "Send me a test notification"}
          </Button>
        </div>
      )}

      {/* Nuclear reset for stuck installs — always visible so users can recover
          from "applicationServerKey is not valid" without deleting the PWA. */}
      {supported && (
        <div className="mt-5 pt-5 border-t border-amber-100">
          <div className="text-[11px] text-slate-600 mb-2 leading-relaxed">
            Stuck on an <code className="font-mono">applicationServerKey is not valid</code> error, or the app feels out of date? Reset the local cache. It logs push out, unregisters the service worker and force-loads the latest code — no need to delete the PWA.
          </div>
          <Button
            data-testid="push-reset-btn"
            onClick={async () => {
              if (!window.confirm("Reset local cache? You'll stay logged in, but the app will reload with fresh code.")) return;
              setBusy(true);
              try {
                await resetAppCache();
                toast.success("Cache cleared. Reloading…");
                setTimeout(() => window.location.reload(), 700);
              } catch (e) {
                toast.error(e.message || "Reset failed");
                setBusy(false);
              }
            }}
            disabled={busy}
            variant="outline"
            className="rounded-xl h-10 px-4 border-red-300 text-red-700 hover:bg-red-50"
          >
            <Trash size={14} weight="bold" className="mr-2" /> Reset app cache & reload
          </Button>
        </div>
      )}
    </div>
  );
}


function AiKeyTab() {
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/ai-key");
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/ai-key", { anthropic_api_key: keyInput.trim() });
      setStatus(r.data);
      setKeyInput("");
      toast.success("Anthropic key saved. AI features will use it immediately.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear the stored key? The server will fall back to the ANTHROPIC_API_KEY environment variable (if set).")) return;
    setBusy(true);
    try {
      const r = await api.put("/settings/ai-key", { anthropic_api_key: "" });
      setStatus(r.data);
      toast.success("Cleared. Falling back to env key.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="glass rounded-2xl p-4 sm:p-6 shimmer-border">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl chrome flex items-center justify-center shrink-0">
            <Sparkle size={22} weight="fill" className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg">Anthropic Claude key</div>
            <p className="text-sm text-slate-600 mt-1">
              Powers AI Insights on the Dashboard and AI slip-verification on payments. Store the key here to avoid touching the server&apos;s <code className="font-mono text-xs bg-white/70 px-1.5 py-0.5 rounded border border-amber-200">.env</code>. Get one at{" "}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="underline text-amber-700 font-semibold">console.anthropic.com</a>.
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-xl bg-white/60 border border-amber-100 p-4" data-testid="ai-key-status">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                status?.configured
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                  : "bg-red-100 text-red-800 border border-red-300"
              }`}
            >
              {status?.configured ? "Configured" : "Not set"}
            </span>
            {status?.configured && (
              <span className="text-[11px] text-slate-600">
                source: <span className="font-mono font-semibold">{status.source === "admin_ui" ? "admin panel" : "server env"}</span>
              </span>
            )}
            {status?.preview && (
              <span className="font-mono text-xs text-slate-700 ml-auto bg-white/70 px-2 py-0.5 rounded border border-amber-100">
                {status.preview}
              </span>
            )}
          </div>
          {!status?.configured && (
            <p className="text-xs text-red-700 mt-2">
              AI Insights and slip verification will fail with a &quot;not configured&quot; error until a key is set.
            </p>
          )}
          {status?.source === "env" && (
            <p className="text-[11px] text-slate-500 mt-2 italic">
              Currently using the <code>ANTHROPIC_API_KEY</code> env variable. Save a new key below to override it without a redeploy.
            </p>
          )}
        </div>

        <div className="mt-5">
          <Label className="text-xs font-semibold tracking-wide">NEW OR REPLACEMENT KEY</Label>
          <Input
            data-testid="ai-key-input"
            type="password"
            placeholder="sk-ant-api03-..."
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            className="mt-1 rounded-xl bg-white/70 border-white/60 h-11 font-mono text-sm"
          />
          <p className="text-[11px] text-slate-500 mt-1.5">
            The key is stored server-side in MongoDB. It is never returned to the browser (only a masked preview is shown above).
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            data-testid="ai-key-save"
            onClick={save}
            disabled={busy || !keyInput.trim()}
            className="amber-pill rounded-xl h-11 px-5"
          >
            {busy ? "Saving…" : "Save key"}
          </Button>
          {status?.source === "admin_ui" && (
            <Button
              data-testid="ai-key-clear"
              onClick={clear}
              disabled={busy}
              variant="outline"
              className="rounded-xl h-11 px-5 border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash size={14} weight="bold" className="mr-2" /> Clear stored key
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
